import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    FormControl,
    Stack,
    FormLabel,
    Box,
    Text,
    Link,
} from '@chakra-ui/react';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import { axios } from '@/services/axios';
import { useDropdownMenuTooltip } from '@/pages/telephony/numberBlocks/add/hooks/useDropdownMenuTooltip';

/**
 * Combined Carrier Field with auto-assign and conditional display
 *
 * Features:
 * - Auto-assigns carrier if only one exists for the wholesaler
 * - Shows single "Carrier" field when inbound and outbound are the same
 * - "Set different inbound/outbound carriers" link expands to show separate fields
 * - "Use same carrier for both" link collapses back to single field
 * - Only visible for scope > 40 (not visible for partners)
 */
const CarrierField = ({
    darkMode,
    values,
    setFieldValue,
    errors = {},
    touched = {},
    requiredScope,
    onError,
    onCarriersLoaded,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const { tooltipPlacement, onMouseDown, onMenuClose } =
        useDropdownMenuTooltip();
    const [hasAutoAssigned, setHasAutoAssigned] = useState(false);
    const [showSeparateFields, setShowSeparateFields] = useState(false);
    const [carriers, setCarriers] = useState([]);
    const [, setIsLoadingCarriers] = useState(false);

    // Determine if component should be visible based on scope
    const isVisible = requiredScope > 40;

    // Determine API endpoint based on wholesaler selection
    const apiEndpoint = useMemo(() => {
        if (values?.wholesalerID) {
            return `/carriers/${values.wholesalerID}`;
        }
        return '/carriers';
    }, [values?.wholesalerID]);

    // Check if inbound and outbound are the same
    const carriersAreSame =
        values?.inboundCarrierID === values?.outboundCarrierID;

    // If carriers are different on load, show separate fields
    useEffect(() => {
        if (
            values?.inboundCarrierID &&
            values?.outboundCarrierID &&
            !carriersAreSame
        ) {
            setShowSeparateFields(true);
        }
    }, [values?.inboundCarrierID, values?.outboundCarrierID, carriersAreSame]);

    // Fetch carriers ONCE and store in state - also auto-assign if only one exists
    useEffect(() => {
        if (!isVisible) return;

        const fetchCarriers = async () => {
            setIsLoadingCarriers(true);
            try {
                const response = await axios.get(apiEndpoint);
                const carrierData = response.data || [];
                setCarriers(carrierData);
                onCarriersLoaded?.(carrierData.length);

                // Auto-assign if only one carrier exists and not already assigned
                if (carrierData.length === 1 && !hasAutoAssigned) {
                    const singleCarrier = carrierData[0];
                    if (!values?.inboundCarrierID) {
                        setFieldValue('inboundCarrierID', singleCarrier.id);
                    }
                    if (!values?.outboundCarrierID) {
                        setFieldValue('outboundCarrierID', singleCarrier.id);
                    }
                    setHasAutoAssigned(true);
                }
            } catch (err) {
                if (onError) onError(err);
            } finally {
                setIsLoadingCarriers(false);
            }
        };

        fetchCarriers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiEndpoint, isVisible]);

    // Reset auto-assign flag and carriers when wholesaler changes
    useEffect(() => {
        setHasAutoAssigned(false);
        setCarriers([]);
    }, [values?.wholesalerID]);

    // Handle single carrier selection (sets both inbound and outbound)
    const handleSingleCarrierChange = useCallback(
        (carrierId) => {
            setFieldValue('inboundCarrierID', carrierId);
            setFieldValue('outboundCarrierID', carrierId);
        },
        [setFieldValue],
    );

    // Handle "Use same carrier for both" click
    const handleUseSameCarrier = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (values?.inboundCarrierID) {
                setFieldValue('outboundCarrierID', values.inboundCarrierID);
            }
            // Delay state change to prevent click from landing on next element after layout reflow
            requestAnimationFrame(() => {
                setShowSeparateFields(false);
            });
        },
        [setFieldValue, values?.inboundCarrierID],
    );

    // Handle "Set different inbound/outbound carriers" click
    const handleSetDifferentCarriers = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Delay state change to prevent accidental clicks on other elements
        requestAnimationFrame(() => {
            setShowSeparateFields(true);
        });
    }, []);

    // Not visible for partners (scope 40) or company (scope 20)
    if (!isVisible) {
        return null;
    }

    // If not showing separate fields, show single "Carrier" field with link
    if (!showSeparateFields && carriersAreSame) {
        return (
            <FormControl
                sx={{ margin: '0 !important' }}
                isInvalid={
                    (!!errors.inboundCarrierID && touched.inboundCarrierID) ||
                    (!!errors.outboundCarrierID && touched.outboundCarrierID)
                }
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling(true)}>
                    <Box>
                        <FormLabel sx={formLabelStyling(darkMode)}>
                            Carrier <span style={{ color: '#e53e3e' }}>*</span>
                        </FormLabel>
                        <Text sx={descriptionStyling} maxWidth={250}>
                            Select the carrier for this number block.
                        </Text>
                        <Link
                            as="button"
                            type="button"
                            color="messenger.500"
                            onClick={handleSetDifferentCarriers}
                            cursor="pointer"
                            fontSize="11px"
                            textDecoration="none"
                            _hover={{ textDecoration: 'none' }}>
                            Set different inbound/outbound carriers
                        </Link>
                    </Box>
                    <Box flex="1" minWidth={0} onMouseDown={onMouseDown}>
                        <DropDown
                            darkMode={darkMode}
                            name="inboundCarrierID"
                            data={carriers}
                            shownValue="name"
                            currentId={values?.inboundCarrierID}
                            handleSelectChange={(item) =>
                                handleSingleCarrierChange(item?.id ?? '')
                            }
                            width="100%"
                            onMenuClose={onMenuClose}
                        />
                    </Box>
                    {isHovered && (
                        <FieldToolTip
                            label="Select the carrier for routing calls"
                            ariaLabel="carrier-tip"
                            left="25px"
                            top="25px"
                            placement={tooltipPlacement}
                        />
                    )}
                </Stack>
            </FormControl>
        );
    }

    // Show two fields: Inbound + Outbound only (when different carriers selected)
    return (
        <>
            {/* Inbound Carrier Field */}
            <FormControl
                sx={{ margin: '0 !important' }}
                isInvalid={
                    !!errors.inboundCarrierID && touched.inboundCarrierID
                }>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling(true)}>
                    <Box>
                        <FormLabel sx={formLabelStyling(darkMode)}>
                            Inbound Carrier{' '}
                            <span style={{ color: '#e53e3e' }}>*</span>
                        </FormLabel>
                        <Text sx={descriptionStyling} maxWidth={250}>
                            Select the inbound carrier for this number block.
                        </Text>
                    </Box>
                    <Box flex="1" minWidth={0}>
                        <DropDown
                            darkMode={darkMode}
                            name="inboundCarrierID"
                            data={carriers}
                            shownValue="name"
                            currentId={values?.inboundCarrierID}
                            handleSelectChange={(item) =>
                                setFieldValue(
                                    'inboundCarrierID',
                                    item?.id ?? '',
                                )
                            }
                            width="100%"
                        />
                    </Box>
                </Stack>
            </FormControl>

            {/* Outbound Carrier Field */}
            <FormControl
                sx={{ margin: '0 !important' }}
                isInvalid={
                    !!errors.outboundCarrierID && touched.outboundCarrierID
                }>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling(true)}>
                    <Box>
                        <FormLabel sx={formLabelStyling(darkMode)}>
                            Outbound Carrier{' '}
                            <span style={{ color: '#e53e3e' }}>*</span>
                        </FormLabel>
                        <Text sx={descriptionStyling} maxWidth={250}>
                            Select the outbound carrier for this number block.
                        </Text>
                        <Link
                            as="button"
                            type="button"
                            color="messenger.500"
                            onClick={handleUseSameCarrier}
                            cursor="pointer"
                            fontSize="11px"
                            textDecoration="none"
                            _hover={{ textDecoration: 'none' }}>
                            Use same carrier for both
                        </Link>
                    </Box>
                    <Box flex="1" minWidth={0}>
                        <DropDown
                            darkMode={darkMode}
                            name="outboundCarrierID"
                            data={carriers}
                            shownValue="name"
                            currentId={values?.outboundCarrierID}
                            handleSelectChange={(item) =>
                                setFieldValue(
                                    'outboundCarrierID',
                                    item?.id ?? '',
                                )
                            }
                            width="100%"
                        />
                    </Box>
                </Stack>
            </FormControl>
        </>
    );
};

export default CarrierField;
